import type {
  ISetupOrdersDto,
  TBusinessType,
  TDeliveryFeeMode,
  TDeliveryRadiusKm,
  TSetupOrdersMode,
  TSetupPayment,
} from '@/interfaces/IStoreSetup';
import { COUNTRIES_MAP } from '@/const/countries.const';
import {
  isPlausiblePhone,
  normalizePhoneInput,
} from '@/domain/reservations/reservation-guide';
import { defaultOrderTypes } from './business-types';
import { formatMoney, parsePriceInput } from './money';
import { mercadoPagoAvailable } from './store-country';
import type { ITextRef } from './setup-flow';

/** The «Pedidos» form (§4.8) before it becomes the `PUT …/orders` body. */
export interface IOrdersForm {
  menuOnly: boolean;
  table: boolean;
  pickup: boolean;
  delivery: boolean;
  feeMode: TDeliveryFeeMode | null;
  /** The amount as typed (parsed with `parsePriceInput`). */
  feeText: string;
  radius: TDeliveryRadiusKm | null;
  payments: TSetupPayment[];
  mercadoPago: boolean;
  mode: TSetupOrdersMode;
  whatsapp: string;
}

export interface IOrdersContext {
  /** The branch already has delivery zones: the reach is not asked. */
  hasZones: boolean;
  country: string;
}

export type TOrdersBlocker = 'types' | 'fee' | 'payments' | 'whatsapp';

/** Payment chips in display order; Mercado Pago only shows a note (never written). */
export const SETUP_PAYMENTS: readonly TSetupPayment[] = [
  'cash',
  'card',
  'transfer',
  'qr',
];

/** The server accepts only these characters in a phone (API `normalizePhone`). */
const PHONE_CHARACTERS = /^\+?[\d\s().-]+$/;

/** Preset from the business type; fee and reach start empty (an invented fee must never be saved). */
export function initialOrdersForm(
  businessType: TBusinessType | null,
  userWhatsapp: string | null
): IOrdersForm {
  const types = defaultOrderTypes(businessType ?? 'restaurant');
  return {
    menuOnly: false,
    table: types.table,
    pickup: types.pickup,
    delivery: types.delivery,
    feeMode: null,
    feeText: '',
    radius: null,
    payments: ['cash'],
    mercadoPago: false,
    mode: 'native',
    whatsapp: userWhatsapp ?? '',
  };
}

/**
 * Client plausibility of the orders WhatsApp number: the server's allowed
 * characters and 6 to 20 digits once `+{telCode}` is added when missing. The
 * server's `normalizePhone` stays the authority.
 */
export function isPlausibleOrdersWhatsapp(
  text: string,
  country: string
): boolean {
  const trimmed = text.trim();
  if (!PHONE_CHARACTERS.test(trimmed)) return false;
  const normalized = normalizePhoneInput(trimmed);
  if (normalized.startsWith('+')) return isPlausiblePhone(normalized);
  const telCode = COUNTRIES_MAP.get(country)?.telCode;
  return isPlausiblePhone(telCode ? `+${telCode}${normalized}` : normalized);
}

/** The delivery fee the form would save: a number, 0 (free), null (to agree), or undefined when not valid yet. */
function deliveryFee(
  form: IOrdersForm,
  country: string
): number | null | undefined {
  switch (form.feeMode) {
    case 'amount':
      return parsePriceInput(form.feeText, country) ?? undefined;
    case 'free':
      return 0;
    case 'tbd':
      return null;
    default:
      return undefined;
  }
}

/** The first unmet rule, in the order the helper text reports them; null = ready to save. */
export function ordersBlocker(
  form: IOrdersForm,
  ctx: IOrdersContext
): TOrdersBlocker | null {
  if (form.menuOnly) return null;
  if (!form.table && !form.pickup && !form.delivery) return 'types';
  if (form.delivery) {
    if (deliveryFee(form, ctx.country) === undefined) return 'fee';
    if (!ctx.hasZones && form.radius === null) return 'fee';
  }
  if (form.payments.length === 0) return 'payments';
  if (
    form.mode === 'wa' &&
    !isPlausibleOrdersWhatsapp(form.whatsapp, ctx.country)
  )
    return 'whatsapp';
  return null;
}

/** Mercado Pago can be offered: available in the country and orders arrive in the panel. */
function offersMercadoPago(country: string, mode: TSetupOrdersMode): boolean {
  return mode === 'native' && mercadoPagoAvailable(country);
}

/**
 * The `PUT /store-setup/:id/orders` body. Menu-only sends no delivery,
 * WhatsApp or Mercado Pago choice (those controls are hidden then).
 */
export function toSetupOrdersDto(
  form: IOrdersForm,
  branchId: number,
  ctx: IOrdersContext
): ISetupOrdersDto {
  const payments = SETUP_PAYMENTS.filter((payment) =>
    form.payments.includes(payment)
  );
  const base = {
    branchId,
    tableOrders: form.table,
    takeAway: form.pickup,
    delivery: form.delivery,
    payments,
  };
  if (form.menuOnly)
    return {
      ...base,
      menuOnly: true,
      deliveryFeeMode: null,
      deliveryFee: null,
      deliveryRadiusKm: null,
      wantsMercadoPago: false,
      ordersMode: 'native',
      ordersWhatsapp: null,
    };
  // Only «amount» sends a fee (the API requires ≥ 0.01): «Gratis» and
  // «A definir» travel in `deliveryFeeMode` alone.
  const fee =
    form.delivery && form.feeMode === 'amount'
      ? deliveryFee(form, ctx.country)
      : undefined;
  return {
    ...base,
    menuOnly: false,
    deliveryFeeMode: form.delivery ? form.feeMode : null,
    deliveryFee: fee ?? null,
    deliveryRadiusKm: form.delivery && !ctx.hasZones ? form.radius : null,
    wantsMercadoPago:
      form.mercadoPago && offersMercadoPago(ctx.country, form.mode),
    ordersMode: form.mode,
    ordersWhatsapp: form.mode === 'wa' ? form.whatsapp.trim() : null,
  };
}

export type TPaymentChipId = TSetupPayment | 'mercadoPago';

export interface IPaymentChip {
  id: TPaymentChipId;
  labelKey: string;
}

/** The payment chips of the country and arrival mode (Mercado Pago only in AR with panel orders). */
export function paymentChips(
  country: string,
  mode: TSetupOrdersMode
): IPaymentChip[] {
  const chips: IPaymentChip[] = SETUP_PAYMENTS.map((payment) => ({
    id: payment,
    labelKey: `storeSetup.orders.${payment}`,
  }));
  if (offersMercadoPago(country, mode))
    chips.push({
      id: 'mercadoPago',
      labelKey: 'storeSetup.orders.mercadoPago',
    });
  return chips;
}

export type TOrderType = 'table' | 'pickup' | 'delivery';

export interface IOrdersPreviewRow {
  type: TOrderType;
  titleKey: string;
  /** Joined with « · » under the title («Envío $ 1.500 · hasta 5 km»). */
  lines: ITextRef[];
}

export interface IOrdersPreviewModel {
  menuOnly: boolean;
  rows: IOrdersPreviewRow[];
  /** Label keys of the chosen payments, in chip order (Mercado Pago excluded); joined with `listJoin`. */
  paymentKeys: string[];
}

function previewLine(key: string, params: ITextRef['params'] = {}): ITextRef {
  return { key, params };
}

function deliveryLines(
  form: IOrdersForm,
  currency: string,
  country: string
): ITextRef[] {
  const lines: ITextRef[] = [];
  const fee = deliveryFee(form, country);
  if (form.feeMode === 'amount' && typeof fee === 'number')
    lines.push(
      previewLine('storeSetup.orders.preview.feeAmount', {
        amount: formatMoney(fee, currency, country),
      })
    );
  else if (form.feeMode === 'free')
    lines.push(previewLine('storeSetup.orders.preview.feeFree'));
  else if (form.feeMode === 'tbd')
    lines.push(previewLine('storeSetup.orders.preview.feeTbd'));
  if (form.radius === 0)
    lines.push(previewLine('storeSetup.orders.preview.noLimit'));
  else if (form.radius !== null)
    lines.push(
      previewLine('storeSetup.orders.preview.radius', { km: form.radius })
    );
  return lines;
}

/** What the diner preview shows for the current form (§4.8, item 5). */
export function ordersPreviewModel(
  form: IOrdersForm,
  currency: string,
  country: string
): IOrdersPreviewModel {
  if (form.menuOnly) return { menuOnly: true, rows: [], paymentKeys: [] };
  const rows: IOrdersPreviewRow[] = [];
  if (form.table)
    rows.push({
      type: 'table',
      titleKey: 'storeSetup.orders.preview.table',
      lines: [previewLine('storeSetup.orders.preview.tableText')],
    });
  if (form.pickup)
    rows.push({
      type: 'pickup',
      titleKey: 'storeSetup.orders.preview.pickup',
      lines: [previewLine('storeSetup.orders.preview.pickupText')],
    });
  if (form.delivery)
    rows.push({
      type: 'delivery',
      titleKey: 'storeSetup.orders.preview.delivery',
      lines: deliveryLines(form, currency, country),
    });
  return {
    menuOnly: false,
    rows,
    paymentKeys: SETUP_PAYMENTS.filter((payment) =>
      form.payments.includes(payment)
    ).map((payment) => `storeSetup.orders.${payment}`),
  };
}
